import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import { styled, css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-date-picker';
import React, { FC, ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons/faCalendar';
import { Styling } from '@/styling';

const commonDatePickerStyle = css`
  width: 100%;
  height: 53px;

  & .react-date-picker {
    width: 100%;
    height: 53px;
    border-radius: ${Styling.radii('input')};
    ${Styling.typography('input')}

    & .react-date-picker__wrapper {
      padding: ${Styling.spacing(1.5, 2)};
      border: 0;
    }

    &--disabled .react-date-picker__inputGroup {
      cursor: not-allowed !important;
    }

    & .react-date-picker__inputGroup {
      cursor: pointer;
    }

    & .react-date-picker__calendar-button {
      margin-left: ${Styling.spacing(1.5)};
      color: ${({ theme }) => theme.palette.primary.main};

      &:focus {
        outline: ${({ theme }) => `1px solid ${theme.palette.primary.main}`};
      }
    }

    & .react-calendar {
      padding: ${Styling.spacing(1.5, 2)};
      border: ${({ theme }) => `1px solid ${theme.palette.grayscale.light2}`};
      border-radius: ${Styling.radii('input')};
      box-shadow: ${Styling.shadow('dropdown')};

      & .react-calendar__tile--now {
        background-color: initial;
      }

      & .react-calendar__tile--active {
        background-color: ${({ theme }) => theme.palette.primary.main};
      }

      & .react-calendar__tile--hasActive {
        background-color: ${({ theme }) => theme.palette.primary.main};
      }
    }
  }
`;

const RoundDatePickerBase = styled.div`
  & .react-date-picker {
    color: ${({ theme }) => theme.palette.primary.text};
    background-color: ${({ theme }) => theme.palette.grayscale.white};
    border: 0;

    &:hover {
      outline: ${({ theme }) => `1px solid ${theme.palette.grayscale.light2}`};
    }

    &--open {
      outline: ${({ theme }) =>
        `1px solid ${theme.palette.primary.main} !important`};
    }

    &--disabled {
      background-color: rgba(255, 255, 255, 0.5);
    }

    & .react-date-picker__inputGroup > input {
      color: ${({ theme }) => theme.palette.primary.text};

      &:focus {
        outline: ${({ theme }) => `1px solid ${theme.palette.primary.main}`};
      }
    }
  }

  ${commonDatePickerStyle}
`;

const FilledDatePickerBase = styled.div`
  & .react-date-picker {
    color: ${({ theme }) => theme.palette.primary.text};
    background-color: ${({ theme }) => theme.palette.grayscale.light5};
    border: 1px solid #e9e9e9;
    transition:
      100ms background-color,
      100ms border;

    &:hover {
      background-color: ${({ theme }) => theme.palette.grayscale.light5};
      outline: ${({ theme }) => `1px solid ${theme.palette.grayscale.light2}`};
    }

    &--open {
      outline: ${({ theme }) =>
        `1px solid ${theme.palette.primary.main} !important`};
    }

    &--disabled {
      background-color: ${({ theme }) => theme.palette.grayscale.light5};
      border: ${({ theme }) => `1px solid ${theme.palette.grayscale.light4}`};
    }

    & .react-date-picker__inputGroup > input {
      color: ${({ theme }) => theme.palette.primary.text};

      &:focus {
        outline: ${({ theme }) => `1px solid ${theme.palette.primary.main}`};
      }
    }
  }

  ${commonDatePickerStyle}
`;

export type DateInputProps = {
  className?: string;
  name?: string;
  value: Date;
  minDate?: Date;
  maxDate?: Date;
  onChange?(value: Date): void;
  disabled?: boolean;
  icon?: ReactNode;
};

const DateInput: FC<DateInputProps> = ({
  name,
  value,
  minDate,
  maxDate,
  onChange,
  disabled = false,
}) => {
  const { i18n } = useTranslation();

  return (
    <DatePicker
      name={name}
      format="y-MM-dd"
      locale={i18n.language}
      disabled={disabled}
      value={value}
      onChange={(value) => onChange?.(value as Date)}
      minDate={minDate}
      maxDate={maxDate}
      calendarIcon={<FontAwesomeIcon icon={faCalendar} size="lg" />}
      clearIcon={null}
    />
  );
};

export const RoundDateInput: FC<DateInputProps> = ({ className, ...props }) => {
  return (
    <RoundDatePickerBase className={className}>
      <DateInput {...props} />
    </RoundDatePickerBase>
  );
};

export const FilledDateInput: FC<DateInputProps> = ({
  className,
  ...props
}) => {
  return (
    <FilledDatePickerBase className={className}>
      <DateInput {...props} />
    </FilledDatePickerBase>
  );
};
